import moment from 'moment-timezone';
import type { LongDateFormatSpec } from 'moment-timezone';

export type DateFormatPreference = 'BROWSER' | 'MDY' | 'DMY' | 'YMD' | 'DMY_DOTS';

export const DEFAULT_DATE_FORMAT_PREFERENCE: DateFormatPreference = 'BROWSER';

type NumericDateFormat = { L: string; l: string };

// `l` is set explicitly, not derived from `L`: moment caches the derived lowercase token, which a
// later updateLocale would carry forward and let shadow `L`.
const DATE_ORDERINGS: Record<Exclude<DateFormatPreference, 'BROWSER'>, NumericDateFormat> = {
  MDY: { L: 'MM/DD/YYYY', l: 'M/D/YYYY' },
  DMY: { L: 'DD/MM/YYYY', l: 'D/M/YYYY' },
  YMD: { L: 'YYYY-MM-DD', l: 'YYYY-MM-DD' },
  DMY_DOTS: { L: 'DD.MM.YYYY', l: 'D.M.YYYY' },
};

export const DATE_FORMAT_PREFERENCES: DateFormatPreference[] = [
  'BROWSER',
  ...(Object.keys(DATE_ORDERINGS) as Array<Exclude<DateFormatPreference, 'BROWSER'>>),
];

export const getDateOrderingPattern = (preference: Exclude<DateFormatPreference, 'BROWSER'>): string =>
  DATE_ORDERINGS[preference].L;

// The same orderings as `DATE_ORDERINGS`, in date-fns tokens, for the Vantage date pipeline (date-fns / Intl), which
// never touches moment and so isn't reached by the moment locale override `applyDateFormatPreference` performs.
// Mirrors the padded `L` form above so an active preset renders identically across the app (e.g. MDY -> 12/31/2025).
const DATE_FNS_ORDERINGS: Record<Exclude<DateFormatPreference, 'BROWSER'>, string> = {
  MDY: 'MM/dd/yyyy',
  DMY: 'dd/MM/yyyy',
  YMD: 'yyyy-MM-dd',
  DMY_DOTS: 'dd.MM.yyyy',
};

export const getDateFnsOrderingPattern = (preference: Exclude<DateFormatPreference, 'BROWSER'>): string =>
  DATE_FNS_ORDERINGS[preference];

const nativeNumericFormats = new Map<string, NumericDateFormat>();

const snapshotNativeNumericFormat = (locale: string): NumericDateFormat => {
  const existing = nativeNumericFormats.get(locale);
  if (existing) {
    return existing;
  }
  const localeData = moment.localeData(locale);
  const snapshot: NumericDateFormat = { L: localeData.longDateFormat('L'), l: localeData.longDateFormat('l') };
  nativeNumericFormats.set(locale, snapshot);
  return snapshot;
};

export const applyDateFormatPreference = (preference: DateFormatPreference): void => {
  const locale = moment.locale();
  const native = snapshotNativeNumericFormat(locale);
  const numericFormat = preference === 'BROWSER' ? native : DATE_ORDERINGS[preference];
  // updateLocale merges, so overriding only L/l suffices; the cast appeases its full-spec type.
  moment.updateLocale(locale, { longDateFormat: numericFormat as LongDateFormatSpec });
};
